import { computed, inject, Service, signal } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { forkJoin } from "rxjs";

import { AdaaHelper } from "../../core/utils";
import { TpFiltersComponent } from "../components";
import { Constants } from "../constants/constants";
import { DataRevisionIds, ProjectStatus } from "../constants/enums";
import {
  ProjectCycle,
  TpDashboardFilter,
  TpDashboardResponse,
  TpDetailedSectorsResponse,
  TpMainSectorsResponse,
  TpOwnerResponse,
  ValueText,
} from "../models";
import { LanguageService } from "./language-service";
import { TpApiService } from "./tp-api-service";
import { TpCyclesApiService } from "./tp-cycles-api-service";
import { TpSectorApiService } from "./tp-sector-api-service";

@Service()
export class TpDashboardService {
  private _tpApiService = inject(TpApiService);
  private _tpSectorApiService = inject(TpSectorApiService);
  private _tpCyclesApiService = inject(TpCyclesApiService);
  private _modalService = inject(NgbModal);
  private _languageService = inject(LanguageService);
  private _translateService = inject(TranslateService);

  currentYear = signal<number>(moment().utcOffset(Constants.uaeTimezone).year());

  dashboardData = signal<TpDashboardResponse | undefined>(undefined);
  mainSectorsData = signal<TpMainSectorsResponse | undefined>(undefined);
  sectorsData = signal<TpDetailedSectorsResponse | undefined>(undefined);
  ownersData = signal<TpOwnerResponse[]>([]);
  dashboardFilters = signal<TpDashboardFilter>({
    status: 0,
    year: undefined,
    cycleId: undefined,
    entityId: undefined,
    ownerId: undefined,
    sectorId: undefined,
    mainSectorId: undefined,
    onlyProjectsOwnedByEntity: false,
    filterBy: undefined,
  });
  dashboardCycles = signal<ValueText[]>([]);
  dashboardYears = signal<ValueText[]>([]);
  dashboardEntities = signal<ValueText[]>([]);
  dashboardSectors = signal<ValueText[]>([]);
  dashboardStatuses = signal<ValueText[]>([]);
  tpDashboardCycles = signal<ProjectCycle[]>([]);

  isFilterApplied = computed<boolean>(() => {
    const filters = this.dashboardFilters();

    return (
      filters.year !== this.currentYear() ||
      filters.status !== ProjectStatus.ALL ||
      AdaaHelper.isDefined(filters.sectorId) ||
      (AdaaHelper.isPMOEntity()
        ? AdaaHelper.isDefined(filters.entityId) && filters.entityId !== Constants.CONSTANT_PMO_ID
        : false)
    );
  });

  public patchFilters(changes: Partial<TpDashboardFilter>): void {
    this.dashboardFilters.update((filters) => ({ ...filters, ...changes }));
  }

  public initDashboard(clearLocalStorage: boolean = false): void {
    this.dashboardData.set(undefined);

    forkJoin([
      this._tpApiService.getProjectEntities(),
      this._tpApiService.getProjectYears(),
      this._tpCyclesApiService.getAllCycles(),
      this._tpSectorApiService.getProjectSectors(),
      this._translateService.get([
        "national_projects.filter.all",
        "national_projects.filter.completed",
        "national_projects.filter.ontime",
        "national_projects.filter.delayed",
        "national_projects.filter.most_completed",
        "national_projects.filter.most_delayed",
        "national_projects.filter.best_target",
        "national_projects.filter.lowest_target",
      ]),
    ]).subscribe({
      next: ([entities, years, cycles, sectors, translation]) => {
        const years$: ValueText[] = [];
        for (let index = years.responseData.minYear; index < years.responseData.maxYear; index++) {
          years$.push({ value: index, text: index.toString() });
        }
        this.dashboardYears.set(years$);

        this.dashboardEntities.set(
          AdaaHelper.setDropdownArray(entities.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );

        this.dashboardSectors.set(
          AdaaHelper.setDropdownArray(sectors.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );

        this.dashboardCycles.set(
          AdaaHelper.setDropdownArray(cycles.responseData, "id", AdaaHelper.getFieldLanguage("name"))
        );
        this.tpDashboardCycles.set(cycles.responseData);

        this.dashboardStatuses.set([
          {
            value: ProjectStatus.ALL,
            text: translation["national_projects.filter.all"],
          },
          {
            value: ProjectStatus.COMPLETED,
            text: translation["national_projects.filter.completed"],
          },
          {
            value: ProjectStatus.ON_TIME,
            text: translation["national_projects.filter.ontime"],
          },
          {
            value: ProjectStatus.DELAYED,
            text: translation["national_projects.filter.delayed"],
          },
          {
            value: ProjectStatus.MOST_COMPLETED,
            text: translation["national_projects.filter.most_completed"],
          },
          {
            value: ProjectStatus.MOST_DELAYED,
            text: translation["national_projects.filter.most_delayed"],
          },
          {
            value: ProjectStatus.BEST_VISION,
            text: translation["national_projects.filter.best_target"],
          },
          {
            value: ProjectStatus.LOWEST_VISION,
            text: translation["national_projects.filter.lowest_target"],
          },
        ]);

        const cycleId = this._getSelectedCycle(cycles.responseData);
        if (cycleId != this.dashboardFilters().cycleId) this.patchFilters({ cycleId });
        if (clearLocalStorage) AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.tpCycle);
      },
    });
  }

  public getData(getTabsData: boolean = true): void {
    if (!AdaaHelper.isDefined(this.dashboardFilters().cycleId)) return;

    this.patchFilters({ entityId: AdaaHelper.entity?.id });

    this.getDashboardData();

    if (AdaaHelper.isPMOEntity() && getTabsData) {
      this._getMainSectorsData();
      this._getOwnersData();
      this._getSectorsData();
    }
  }

  public openFilters(showStatusFilter: boolean = true) {
    const modal = this._modalService.open(TpFiltersComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.dashboardFilters = this.dashboardFilters();
    modal.componentInstance.years = this.dashboardYears();
    modal.componentInstance.entities = this.dashboardEntities();
    modal.componentInstance.sectors = this.dashboardSectors();
    modal.componentInstance.statuses = this.dashboardStatuses();
    modal.componentInstance.showStatusFilter = showStatusFilter;

    modal.result.then((e) => {
      if (e === true) {
        this.resetFilters();
      } else if (e === undefined) {
        return;
      } else {
        this.patchFilters({
          ...(showStatusFilter ? { status: e.status } : {}),
          year: e.year,
          entityId: e.entityId,
          sectorId: e.sectorId,
        });

        this.getDashboardData();
      }
    });
  }

  public resetFilters(showStatusFilter: boolean = true, fetchData: boolean = true) {
    this.patchFilters({
      ...(showStatusFilter ? { status: 0 } : {}),
      year: this.currentYear(),
      entityId: AdaaHelper.entity?.id,
      sectorId: undefined,
      ownerId: undefined,
    });

    fetchData && this.getDashboardData();
  }

  public getDashboardData(): void {
    const filters = this.dashboardFilters();
    const cycle = this.tpDashboardCycles().find((c) => c.id === filters.cycleId);
    const useDS3Endpoint = cycle?.dataRevisionId === DataRevisionIds.DS3;
    this._tpApiService.getTpDashboard(filters, useDS3Endpoint).subscribe((response) => {
      if (response.inError) return;

      this._formatDashboardData(response.responseData);
    });
  }

  private _getMainSectorsData(): void {
    this._tpApiService.getTpMainSectors(this.dashboardFilters().cycleId).subscribe((response) => {
      if (response.inError) return;

      this.mainSectorsData.set(response.responseData);
    });
  }

  private _getSectorsData(): void {
    this._tpApiService.getTpSectors(this.dashboardFilters().cycleId).subscribe((response) => {
      if (response.inError) return;

      this.sectorsData.set(response.responseData);
    });
  }

  private _getOwnersData(): void {
    const filters = this.dashboardFilters();

    this._tpApiService.getTpOwners(filters.cycleId, filters.year).subscribe((response) => {
      if (response.inError) return;

      this.ownersData.set(response.responseData);
    });
  }

  private _formatDashboardData(data: TpDashboardResponse): void {
    data.completedWidth = data.nationalProjectCount ? (data.completedCount * 100) / data.nationalProjectCount : 0;
    data.ongoingWidth = data.nationalProjectCount ? (data.ongoingCount * 100) / data.nationalProjectCount : 0;
    data.delayedWidth = data.nationalProjectCount ? (data.delayedCount * 100) / data.nationalProjectCount : 0;
    data.onTrackWidth = data.nationalProjectCount ? (data.ontrackCount * 100) / data.nationalProjectCount : 0;
    data.needImprovementWidth = data.nationalProjectCount
      ? (data.needImprovementCount * 100) / data.nationalProjectCount
      : 0;
    data.notStartedWidth = data.nationalProjectCount ? (data.notStartedCount * 100) / data.nationalProjectCount : 0;

    data.nationalProjects.forEach((e) => (e.projectStatusId = this._getColorId(e.projectStatus)));

    this.dashboardData.set(data);
  }

  private _getColorId(status: string): number {
    switch (status) {
      case "COMPLETED":
        return 33;
      case "ON_GOING":
        return 35;
      case "DELAYED":
        return 34;
      case "NOT_STARTED":
        return 34;
      default: //CANCELED
        return 72;
    }
  }

  private _getSelectedCycle(cycles: ProjectCycle[]): number {
    if (AdaaHelper.isDefined(AdaaHelper.getLocalStorage(Constants.localStorageKeys.tpCycle)))
      return +AdaaHelper.getLocalStorage(Constants.localStorageKeys.tpCycle);

    //Get the selected cycle
    cycles.sort((a, b) => b.startDate - a.startDate);

    const currentDate = moment();
    for (const element of cycles) {
      const convertedDate = moment.unix(element.startDate / 1000).add(30, "days");

      if (currentDate > convertedDate) {
        return +element.id;
      }
    }
    return 0;
  }
}
